import { useTranslation } from "react-i18next";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";

interface FunnelKpis {
  agendados: number;
  confirmados: number;
  finalizados: number;
  pagos: number;
  receitaTotal: number;
}

interface AppointmentsFunnelProps {
  kpis: FunnelKpis | null;
  isLoading: boolean;
  formatCurrency: (v: number) => string;
}

interface FunnelStep {
  index: number;
  label: string;
  sub: string;
  value: number;
  displayValue: string;
  conversionFromPrev: number | null;
}

export function AppointmentsFunnel({ kpis, isLoading, formatCurrency }: AppointmentsFunnelProps) {
  const { t } = useTranslation("reports");

  const baseSteps: FunnelStep[] = [
    {
      index: 1,
      label: t("scheduled"),
      sub: t("funnelStep1Sub"),
      value: kpis?.agendados ?? 0,
      displayValue: kpis ? String(kpis.agendados) : "–",
      conversionFromPrev: null,
    },
    {
      index: 2,
      label: t("confirmed"),
      sub: t("funnelStep2Sub"),
      value: kpis?.confirmados ?? 0,
      displayValue: kpis ? String(kpis.confirmados) : "–",
      conversionFromPrev: kpis && kpis.agendados > 0 ? (kpis.confirmados / kpis.agendados) * 100 : null,
    },
    {
      index: 3,
      label: t("finished"),
      sub: t("funnelStep3Sub"),
      value: kpis?.finalizados ?? 0,
      displayValue: kpis ? String(kpis.finalizados) : "–",
      conversionFromPrev: kpis && kpis.confirmados > 0 ? (kpis.finalizados / kpis.confirmados) * 100 : null,
    },
    {
      index: 4,
      label: t("paid"),
      sub: t("funnelStep4Sub"),
      value: kpis?.pagos ?? 0,
      displayValue: kpis ? String(kpis.pagos) : "–",
      conversionFromPrev: kpis && kpis.finalizados > 0 ? (kpis.pagos / kpis.finalizados) * 100 : null,
    },
    {
      index: 5,
      label: t("totalRevenue"),
      sub: t("funnelStep5Sub"),
      value: kpis?.receitaTotal ?? 0,
      displayValue: kpis ? formatCurrency(kpis.receitaTotal) : "–",
      conversionFromPrev: null,
    },
  ];

  const steps = isLoading
    ? baseSteps.map((step) => ({ ...step, value: step.index === 1 ? 100 : 72 - step.index * 7 }))
    : kpis
      ? baseSteps
      : [];

  const maxCountValue = Math.max(...steps.slice(0, 4).map((step) => step.value), 1);
  const stepColors = [
    "hsl(var(--funnel-scheduled))",
    "hsl(var(--funnel-confirmed))",
    "hsl(var(--funnel-finished))",
    "hsl(var(--funnel-paid))",
    "hsl(var(--funnel-revenue))",
  ];

  function convColor(pct: number | null) {
    if (pct === null) return "";
    if (pct >= 92) return "text-green-600 dark:text-green-400";
    if (pct >= 70) return "text-amber-600 dark:text-amber-400";
    return "text-red-600 dark:text-red-400";
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">{t("funnelTitle")}</CardTitle>
        <CardDescription>{t("funnelDesc")}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {steps.map((step) => {
            const isRevenue = step.index === 5;
            const barWidth = isLoading
              ? step.value
              : isRevenue
                ? step.value > 0 ? 100 : 4
                : maxCountValue > 0 ? Math.max(4, (step.value / maxCountValue) * 100) : 4;
            return (
              <div key={step.index} className="grid grid-cols-[1.75rem_minmax(5.25rem,7.5rem)_minmax(4rem,1fr)_4.5rem] items-center gap-2 py-1.5 sm:grid-cols-[1.75rem_minmax(7.5rem,10rem)_minmax(8rem,1fr)_5.5rem_3rem] sm:gap-3">
                {/* Step number */}
                <div className="shrink-0 h-6 w-6 rounded-full border border-border/70 bg-background flex items-center justify-center text-xs font-semibold text-muted-foreground">
                  {step.index}
                </div>

                {/* Name + sub */}
                <div className="min-w-0">
                  <p className="text-sm font-medium leading-tight">{step.label}</p>
                  <p className="text-[11px] text-muted-foreground leading-tight">{step.sub}</p>
                </div>

                {/* Bar */}
                <div className="min-w-0 h-3 rounded-full bg-muted/70 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ backgroundColor: stepColors[step.index - 1], width: `${barWidth}%` }}
                  />
                </div>

                {/* Count */}
                <div className="shrink-0 text-sm font-semibold tabular-nums text-right">
                  {step.displayValue}
                </div>

                {/* Conversion */}
                <div className={`hidden shrink-0 text-xs tabular-nums text-right sm:block ${convColor(step.conversionFromPrev)}`}>
                  {step.conversionFromPrev !== null
                    ? `${step.conversionFromPrev.toFixed(0)}%`
                    : ""}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
